/**
 * Site Authentication API
 * Handles simple site-wide password authentication
 */

import axios from 'axios';
import { API_BASE_URL } from '@/utils/constants';

/**
 * Site auth status response
 */
export interface SiteAuthStatus {
  authEnabled: boolean;
  authenticated: boolean;
  isAdmin?: boolean;
}

/**
 * Site login response
 */
export interface SiteLoginResponse {
  success: boolean;
  message: string;
  error?: string;
  isAdmin?: boolean;
}

/**
 * Create a separate axios instance for site auth that includes cookies
 */
const siteAuthClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  withCredentials: true, // Important: include cookies in requests
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Check site authentication status
 */
export async function getSiteAuthStatus(): Promise<SiteAuthStatus> {
  const response = await siteAuthClient.get<SiteAuthStatus>('/site-auth/status');
  return response.data;
}

/**
 * Login with email and password
 */
export async function siteLogin(email: string, password: string): Promise<SiteLoginResponse> {
  const response = await siteAuthClient.post<SiteLoginResponse>('/site-auth/login', {
    email,
    password,
  });
  return response.data;
}

/**
 * Bypass login in development mode
 */
export async function siteDevBypass(): Promise<SiteLoginResponse> {
  const response = await siteAuthClient.post<SiteLoginResponse>('/site-auth/dev-bypass');
  return response.data;
}

/**
 * Logout from site
 */
export async function siteLogout(): Promise<SiteLoginResponse> {
  const response = await siteAuthClient.post<SiteLoginResponse>('/site-auth/logout');
  return response.data;
}
